/**
 * TET À TET — GLOBAL.JS
 * Nav · Scroll reveal · Sticky header · Safe external links
 * No dependencies. Loaded with `defer`.
 */
(function () {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- 1. MOBILE NAVIGATION ---------------- */
  const nav = {
    toggle: null,
    menu: null,
    init() {
      this.toggle = document.querySelector('[data-nav-toggle]');
      this.menu   = document.querySelector('[data-nav-menu]');
      if (!this.toggle || !this.menu) return;

      this.toggle.addEventListener('click', () => this.toggleMenu());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.closeMenu();
      });

      this.menu.addEventListener('click', (e) => {
        const link = e.target.closest('a');
        if (link && this.isOpen()) this.closeMenu();
      });
    },
    isOpen() { return this.menu.classList.contains('is-open'); },
    toggleMenu() { this.isOpen() ? this.closeMenu() : this.openMenu(); },
    openMenu() {
      this.menu.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('no-scroll');
    },
    closeMenu() {
      this.menu.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('no-scroll');
    }
  };

  /* ---------------- 2. SCROLL REVEAL ---------------- */
  const reveal = {
    init() {
      const items = document.querySelectorAll('[data-reveal]');
      if (!items.length) return;

      if (reducedMotion || !('IntersectionObserver' in window)) {
        items.forEach(el => el.classList.add('is-visible'));
        return;
      }

      const io = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });

      items.forEach(el => io.observe(el));
    }
  };

  /* ---------------- 3. STICKY HEADER ---------------- */
  const stickyHeader = {
    el: null,
    ticking: false,
    init() {
      this.el = document.querySelector('[data-site-header]');
      if (!this.el) return;
      window.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.onScroll();
    },
    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.el.classList.toggle('is-scrolled', window.pageYOffset > 24);
        this.ticking = false;
      });
    }
  };

  /* ---------------- 4. VIEWPORT HEIGHT FIX ---------------- */
  const vhFix = {
    init() {
      const set = () => {
        document.documentElement.style.setProperty(
          '--vh', (window.innerHeight * 0.01) + 'px'
        );
      };
      set();
      window.addEventListener('resize', set, { passive: true });
      window.addEventListener('orientationchange', set);
    }
  };

  /* ---------------- 5. EXTERNAL LINK SAFETY ---------------- */
  const externalLinks = {
    init() {
      const host = window.location.hostname;
      document.querySelectorAll('a[href^="http"]').forEach(a => {
        try {
          const url = new URL(a.href);
          if (url.hostname === host) return;
          if (!a.hasAttribute('target')) a.setAttribute('target', '_blank');
          const rel = new Set((a.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
          rel.add('noopener'); rel.add('noreferrer');
          a.setAttribute('rel', [...rel].join(' '));
        } catch { /* ignore */ }
      });
    }
  };

  /* ---------------- BOOT ---------------- */
  const boot = () => {
    nav.init();
    reveal.init();
    stickyHeader.init();
    vhFix.init();
    externalLinks.init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.TetATet = Object.freeze({
    reducedMotion,
    $: (sel, root = document) => root.querySelector(sel),
    $$: (sel, root = document) => [...root.querySelectorAll(sel)]
  });
})();
